import type { PanelScope } from '../context/types';

/**
 * How the tools panel announces that its content changed.
 *
 * The axis carries the meaning, not the decoration: vertical means the step
 * rail moved the panel, horizontal means the panel changed *level*. The sign
 * says which way — `down`/`up` follow the rail's order, `fwd`/`back` follow the
 * breadcrumb's reading order.
 *
 * `label` is the cheap one, and the reason the whole thing stays calm: the
 * panel is already showing a block's actions and the user clicks another block,
 * so only the name in the header changed. Animating the body there would make
 * it breathe on every single canvas click.
 */
export type PanelMotion = 'fwd' | 'back' | 'down' | 'up' | 'label';

export type PanelSnapshot = {
    stepId: string | null;
    stepIndex: number;
    /** `null` = level 1. Identity of the scope, not the object reference. */
    scopeKey: string | null;
};

/**
 * Collapses a scope to the things a *reader* would notice changing. Two scopes
 * with the same anchor, focus and label look identical in the panel, so a new
 * object with the same values must not count as a change.
 */
export const panelScopeKey = (scope: PanelScope | null): string | null =>
    scope ? `${scope.anchorRealId ?? ''}|${scope.focusedRealId ?? ''}|${scope.label}` : null;

/**
 * The change budget. Returns `null` when nothing a reader would notice changed —
 * the common case on a re-render, and the one that must stay silent.
 *
 * Step changes win over scope changes: switching steps replaces the panel
 * wholesale, and the scope going stale underneath is a consequence, not a
 * second event.
 */
export function decidePanelMotion(prev: PanelSnapshot, next: PanelSnapshot): PanelMotion | null {
    if (prev.stepId !== next.stepId) {
        // Equal indices can only happen if a step was inserted or removed under
        // us; `down` is the safer read, since forward is the common direction.
        return next.stepIndex >= prev.stepIndex ? 'down' : 'up';
    }

    if (prev.scopeKey === next.scopeKey) return null;
    if (prev.scopeKey === null) return 'fwd';
    if (next.scopeKey === null) return 'back';

    return 'label';
}
